import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import CircularProgress from "@mui/material/CircularProgress";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMore from "@mui/icons-material/ExpandMore";
import Snackbar from "@mui/material/Snackbar";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Tooltip from "@mui/material/Tooltip";
import { observer } from "mobx-react-lite";
import { useState, useContext, useEffect, useMemo } from "react";
import { useIntl } from "react-intl";
import styled from "@mui/material/styles/styled";
import { RunnerData } from "../../utils/getPromptJson";
import getPromptJson from "../../utils/getPromptJson";
import { AppContext } from "../../contexts/AppContext";
import { Plan, SectionData } from "../../services/PlansService";
import { convertDistanceMiles } from "../../utils/UnitsUtils";

const ContentBox = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column"
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
    boxShadow: "none",
    border: `1px solid ${theme.palette.divider}`,
    "&:before": {
        display: "none"
    },
    marginBottom: theme.spacing(1)
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    minHeight: 48,
    "&.Mui-expanded": {
        minHeight: 48
    }
}));

interface SectionInsight {
    section_number: number;
    insight: string;
}

interface AICoachResponse {
    overall_feedback: string;
    section_insights: SectionInsight[];
    nutrition_hydration_feedback: string;
    gear_feedback: string;
    risks: string[];
    actionable_adjustments: string[];
}

export interface AICoachBoxProps {
    plan: Plan;
    sectionsData: SectionData[];
    currentSection?: number;
    sectionType?: "start" | "end" | "control" | "section";
}

const AICoachBox: React.FC<AICoachBoxProps> = observer(({ plan, sectionsData, currentSection, sectionType }) => {
    const intl = useIntl();
    const { userService, analysisService, nutritionProductsService, ordersService } = useContext(AppContext);
    const [screen, setScreen] = useState<"intro" | "form" | "loading" | "results">("intro");
    const [aiResponse, setAiResponse] = useState<AICoachResponse | null>(null);
    const [hasLoadedAnalysis, setHasLoadedAnalysis] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [rateLimitMinutes, setRateLimitMinutes] = useState<number>(0);
    const [usageLimitReached, setUsageLimitReached] = useState<boolean>(false);
    const [usageCount, setUsageCount] = useState<number>(0);
    const [usageLimit, setUsageLimit] = useState<number>(0);
    const [limitsDialogOpen, setLimitsDialogOpen] = useState<boolean>(false);

    const distanceUnits = userService.user?.distanceUnits || "km";
    const elevationUnits = userService.user?.elevationUnits || "m";
    const subscriptionType = ordersService.subscriptionType;

    const [experienceLevel, setExperienceLevel] = useState<"beginner" | "intermediate" | "advanced">("intermediate");
    const [primaryGoal, setPrimaryGoal] = useState<string>("");
    const [secondaryGoal, setSecondaryGoal] = useState<string>("");
    const [avgWeeklyDistance, setAvgWeeklyDistance] = useState<string>("");
    const [avgWeeklyVert, setAvgWeeklyVert] = useState<string>("");
    const [longestRun, setLongestRun] = useState<string>("");

    const queryType = useMemo(() => {
        console.log("Determining query type", { pe: plan.plannedEffort, sectionsData });
        if (sectionsData.find((s, index) => s.type === "section" && (plan.plannedEffort.length < index || !plan.plannedEffort[index].time))) {
            return "initial";
        }
        return "full";
    }, [sectionsData, plan.plannedEffort]);

    console.log("AICoachBox render", { planId: plan.id, screen, aiResponse, queryType });

    // Check limits and update countdown
    useEffect(() => {
        const checkLimits = async () => {
            if (!userService.user?.id || !plan.id) return;

            // Check rate limit
            const rateLimitResult = await analysisService.checkRateLimit(
                userService.user.id,
                plan.id
            );
            setRateLimitMinutes(rateLimitResult.minutesUntilAvailable);

            // Check usage limit
            const usageLimitResult = await analysisService.checkUsageLimit(
                userService.user.id,
                subscriptionType
            );
            setUsageLimitReached(!usageLimitResult.canRequest);
            setUsageCount(usageLimitResult.usageCount);
            setUsageLimit(usageLimitResult.limit);
        };

        checkLimits();

        // Update countdown every minute if rate limited
        const interval = setInterval(() => {
            if (rateLimitMinutes > 0) {
                setRateLimitMinutes(prev => Math.max(0, prev - 1));
            }
        }, 60000); // Every minute

        return () => clearInterval(interval);
    }, [userService.user?.id, plan.id, analysisService, subscriptionType, aiResponse]);

    // Load most recent analysis on mount
    useEffect(() => {
        const loadAnalysis = async () => {
            if (!userService.user?.id || !plan.id || hasLoadedAnalysis) return;

            setScreen("loading");
            setError(null);

            const analysis = await analysisService.loadMostRecentAnalysis(
                userService.user.id,
                plan.id
            );

            if (analysis && analysis.response) {
                setAiResponse(analysis.response);
                setScreen("results");
            } else {
                // Check if there was an error during loading
                if (analysisService.error) {
                    setError("aiCoach.errorLoading");
                }
                setScreen("intro");
            }
            setHasLoadedAnalysis(true);
        };

        loadAnalysis();
    }, [userService.user?.id, plan.id, hasLoadedAnalysis, analysisService]);

    const handleStart = () => {
        setScreen("form");
    };

    const handleStartOver = () => {
        // If limits are reached, show dialog explaining why
        if (usageLimitReached || rateLimitMinutes > 0) {
            setLimitsDialogOpen(true);
            return;
        }

        // Otherwise, go back to intro
        setScreen("intro");
    };

    const handleSubmitClick = async () => {
        // If limits are reached, show dialog explaining why
        if (usageLimitReached || rateLimitMinutes > 0) {
            setLimitsDialogOpen(true);
            return;
        }

        // Otherwise, run the AI coach directly
        await handleSubmit();
    };

    const handleCloseLimitsDialog = () => {
        setLimitsDialogOpen(false);
    };

    const handleSubmit = async () => {
        // Convert distance from miles to km if needed
        const avgWeeklyKm = distanceUnits === "miles"
            ? convertDistanceMiles(parseFloat(avgWeeklyDistance) || 0, "km", false)
            : parseFloat(avgWeeklyDistance) || 0;

        const longestRunKm = distanceUnits === "miles"
            ? convertDistanceMiles(parseFloat(longestRun) || 0, "km", false)
            : parseFloat(longestRun) || 0;

        // Convert elevation from feet to meters if needed
        const avgWeeklyVertM = elevationUnits === "feet"
            ? (parseFloat(avgWeeklyVert) || 0) / 3.28084
            : parseFloat(avgWeeklyVert) || 0;

        const runnerData: RunnerData = {
            experience_level: experienceLevel,
            goals: {
                primary: primaryGoal,
                secondary: secondaryGoal
            },
            training_background: {
                avg_weekly_km: avgWeeklyKm,
                avg_weekly_vert_m: avgWeeklyVertM,
                longest_run_km: longestRunKm
            },
            units_for_response: {
                distance: distanceUnits === "miles" ? "miles" : "km",
                elevation: elevationUnits === "feet" ? "feet" : "meters"
            }
        };

        if (!userService.user?.id) {
            console.error("User not authenticated");
            return;
        }

        // Show loading
        setScreen("loading");

        // Generate the prompt using getPromptJson
        const promptData = getPromptJson(
            plan,
            sectionsData,
            runnerData,
            nutritionProductsService.nutritionProducts,
            queryType
        );

        // Get user language
        const language = userService.user.language || "en";

        // Call the analysis service
        const analysis = await analysisService.getRaceStrategy(
            userService.user.id,
            plan.id,
            promptData,
            language,
            queryType
        );

        if (analysis && analysis.response) {
            setAiResponse(analysis.response);
            setScreen("results");
        } else {
            // Handle error - show error and stay on form
            if (analysisService.error) {
                setError("aiCoach.errorGenerating");
            }
            setScreen("form");
        }
    };

    const renderContent = () => {
        if (screen === "intro") {
            return (
                <ContentBox>
                    <Box flex={1} display="flex" flexDirection="column" justifyContent="center" alignItems="center" textAlign="center" gap={3}>
                        <Typography variant="body2" fontWeight="bold">
                            {intl.formatMessage({ id: "aiCoach.title" })}
                        </Typography>
                        <Typography variant="body2" maxWidth={400}>
                            {intl.formatMessage({ id: "aiCoach.introText" })}
                        </Typography>
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleStart}
                            fullWidth
                        >
                            {intl.formatMessage({ id: "aiCoach.start" })}
                        </Button>
                    </Box>
                </ContentBox>
            );
        }

        if (screen === "loading") {
            return (
                <ContentBox>
                    <Box flex={1} display="flex" flexDirection="column" justifyContent="center" alignItems="center" gap={2}>
                        <CircularProgress />
                        <Typography variant="body2">
                            {intl.formatMessage({ id: "aiCoach.analyzing" })}
                        </Typography>
                    </Box>
                </ContentBox>
            );
        }

        if (screen === "results" && aiResponse) {
            // Get current section insight if we're on a section
            const currentSectionInsight = aiResponse.section_insights.find(s => s.section_number === (currentSection || 0) + 1);

            return (
                <Box p={1} overflow="auto">
                    <StyledAccordion defaultExpanded>
                        <StyledAccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="body2" fontWeight="bold">
                                {intl.formatMessage({ id: "aiCoach.overallFeedback" })}
                            </Typography>
                        </StyledAccordionSummary>
                        <AccordionDetails>
                            <Typography variant="body2">{aiResponse.overall_feedback}</Typography>
                        </AccordionDetails>
                    </StyledAccordion>

                    {currentSectionInsight && (
                        <StyledAccordion>
                            <StyledAccordionSummary expandIcon={<ExpandMore />}>
                                <Typography variant="body2" fontWeight="bold">
                                    {intl.formatMessage({ id: sectionType === "control" ? "aiCoach.aidStationInsight" : sectionType === "start" ? "aiCoach.startInsight" : sectionType === "end" ? "aiCoach.endInsight" : "aiCoach.sectionInsight" })}
                                </Typography>
                            </StyledAccordionSummary>
                            <AccordionDetails>
                                <Typography variant="body2">{currentSectionInsight.insight}</Typography>
                            </AccordionDetails>
                        </StyledAccordion>
                    )}

                    <StyledAccordion>
                        <StyledAccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="body2" fontWeight="bold">
                                {intl.formatMessage({ id: "aiCoach.nutritionHydrationFeedback" })}
                            </Typography>
                        </StyledAccordionSummary>
                        <AccordionDetails>
                            <Typography variant="body2">{aiResponse.nutrition_hydration_feedback}</Typography>
                        </AccordionDetails>
                    </StyledAccordion>

                    <StyledAccordion>
                        <StyledAccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="body2" fontWeight="bold">
                                {intl.formatMessage({ id: "aiCoach.gearFeedback" })}
                            </Typography>
                        </StyledAccordionSummary>
                        <AccordionDetails>
                            <Typography variant="body2">{aiResponse.gear_feedback}</Typography>
                        </AccordionDetails>
                    </StyledAccordion>

                    <StyledAccordion>
                        <StyledAccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="body2" fontWeight="bold">
                                {intl.formatMessage({ id: "aiCoach.risks" })}
                            </Typography>
                        </StyledAccordionSummary>
                        <AccordionDetails>
                            <Box component="ul" sx={{ pl: 2, my: 0 }}>
                                {aiResponse.risks.map((risk, index) => (
                                    <Typography component="li" variant="body2" key={index} sx={{ mb: 1 }}>
                                        {risk}
                                    </Typography>
                                ))}
                            </Box>
                        </AccordionDetails>
                    </StyledAccordion>

                    <StyledAccordion>
                        <StyledAccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="body2" fontWeight="bold">
                                {intl.formatMessage({ id: "aiCoach.actionableAdjustments" })}
                            </Typography>
                        </StyledAccordionSummary>
                        <AccordionDetails>
                            <Box component="ul" sx={{ pl: 2, my: 0 }}>
                                {aiResponse.actionable_adjustments.map((adjustment, index) => (
                                    <Typography component="li" variant="body2" key={index} sx={{ mb: 1 }}>
                                        {adjustment}
                                    </Typography>
                                ))}
                            </Box>
                        </AccordionDetails>
                    </StyledAccordion>

                    <Box mt={2} display="flex" justifyContent="center">
                        <Tooltip
                            title={
                                usageLimitReached
                                    ? intl.formatMessage({ id: "aiCoach.usageLimitReached" })
                                    : rateLimitMinutes > 0
                                        ? intl.formatMessage({ id: "aiCoach.rateLimitTooltip" }, { minutes: rateLimitMinutes })
                                        : usageLimit > 0 && subscriptionType
                                            ? intl.formatMessage(
                                                { id: analysisService.getUsageLimits(subscriptionType).period === "month" ? "aiCoach.usageTooltipMonth" : "aiCoach.usageTooltipYear" },
                                                { count: usageLimit - usageCount }
                                            )
                                            : ""
                            }
                            arrow
                        >
                            <Box component="span" sx={{ display: "inline-block" }}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={handleStartOver}
                                    disabled={usageLimitReached || rateLimitMinutes > 0}
                                >
                                    {intl.formatMessage({ id: "aiCoach.startOver" })}
                                </Button>
                            </Box>
                        </Tooltip>
                    </Box>
                </Box>
            );
        }

        return (
            <ContentBox>
                <Typography variant="body2" fontWeight="bold" mb={2}>
                    {intl.formatMessage({ id: "aiCoach.formTitle" })}
                </Typography>

                <Box display="flex" flexDirection="column" gap={2}>
                    <FormControl fullWidth>
                        <InputLabel id="experience-level-label">
                            {intl.formatMessage({ id: "aiCoach.experienceLevel" })}
                        </InputLabel>
                        <Select
                            labelId="experience-level-label"
                            value={experienceLevel}
                            label={intl.formatMessage({ id: "aiCoach.experienceLevel" })}
                            onChange={(e) => setExperienceLevel(e.target.value as "beginner" | "intermediate" | "advanced")}
                        >
                            <MenuItem value="beginner">
                                {intl.formatMessage({ id: "aiCoach.beginner" })}
                            </MenuItem>
                            <MenuItem value="intermediate">
                                {intl.formatMessage({ id: "aiCoach.intermediate" })}
                            </MenuItem>
                            <MenuItem value="advanced">
                                {intl.formatMessage({ id: "aiCoach.advanced" })}
                            </MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        fullWidth
                        label={intl.formatMessage({ id: "aiCoach.primaryGoal" })}
                        value={primaryGoal}
                        onChange={(e) => setPrimaryGoal(e.target.value)}
                        multiline
                        rows={2}
                        placeholder={intl.formatMessage({ id: "aiCoach.primaryGoalPlaceholder" })}
                    />

                    <TextField
                        fullWidth
                        label={intl.formatMessage({ id: "aiCoach.secondaryGoal" })}
                        value={secondaryGoal}
                        onChange={(e) => setSecondaryGoal(e.target.value)}
                        multiline
                        rows={2}
                        placeholder={intl.formatMessage({ id: "aiCoach.secondaryGoalPlaceholder" })}
                    />

                    <Typography variant="subtitle2" fontWeight="bold" mt={1}>
                        {intl.formatMessage({ id: "aiCoach.trainingBackground" })}
                    </Typography>

                    <TextField
                        fullWidth
                        label={intl.formatMessage(
                            { id: distanceUnits === "miles" ? "aiCoach.avgWeeklyMiles" : "aiCoach.avgWeeklyKm" }
                        )}
                        value={avgWeeklyDistance}
                        onChange={(e) => setAvgWeeklyDistance(e.target.value)}
                        inputProps={{ inputMode: "numeric" }}
                    />

                    <TextField
                        fullWidth
                        label={intl.formatMessage(
                            { id: elevationUnits === "feet" ? "aiCoach.avgWeeklyVertFeet" : "aiCoach.avgWeeklyVertM" }
                        )}
                        value={avgWeeklyVert}
                        onChange={(e) => setAvgWeeklyVert(e.target.value)}
                        inputProps={{ inputMode: "numeric" }}
                    />

                    <TextField
                        fullWidth
                        label={intl.formatMessage(
                            { id: distanceUnits === "miles" ? "aiCoach.longestRunMiles" : "aiCoach.longestRunKm" }
                        )}
                        value={longestRun}
                        onChange={(e) => setLongestRun(e.target.value)}
                        inputProps={{ inputMode: "numeric" }}
                    />

                    <Box display="flex" gap={2} mt={2}>
                        <Box flex={1}>
                            <Button
                                variant="outlined"
                                onClick={() => setScreen("intro")}
                                fullWidth
                                size="small"
                            >
                                {intl.formatMessage({ id: "action.back" })}
                            </Button>
                        </Box>
                        <Box flex={1}>
                            <Tooltip
                                title={
                                    usageLimitReached
                                        ? intl.formatMessage({ id: "aiCoach.usageLimitReached" })
                                        : rateLimitMinutes > 0
                                            ? intl.formatMessage({ id: "aiCoach.rateLimitTooltip" }, { minutes: rateLimitMinutes })
                                            : usageLimit > 0 && subscriptionType
                                                ? intl.formatMessage(
                                                    { id: analysisService.getUsageLimits(subscriptionType).period === "month" ? "aiCoach.usageTooltipMonth" : "aiCoach.usageTooltipYear" },
                                                    { count: usageLimit - usageCount }
                                                )
                                                : ""
                                }
                                arrow
                                disableHoverListener={usageLimit === 0 || !subscriptionType}
                            >
                                <Button
                                    variant="contained"
                                    onClick={handleSubmitClick}
                                    fullWidth
                                    size="small"
                                    disabled={!primaryGoal || !avgWeeklyDistance || !longestRun}
                                >
                                    {intl.formatMessage({ id: "aiCoach.submit" })}
                                </Button>
                            </Tooltip>
                        </Box>
                    </Box>
                </Box>
            </ContentBox>
        );
    };

    const getLimitsDialogMessage = () => {
        if (!subscriptionType) return "";

        const limitsKey = `aiCoach.limitsDialog${subscriptionType.charAt(0).toUpperCase() + subscriptionType.slice(1)}`;
        const limits = analysisService.getUsageLimits(subscriptionType);

        return (
            <>
                <DialogContentText>
                    {intl.formatMessage({ id: limitsKey })}
                </DialogContentText>
                <DialogContentText sx={{ mt: 2 }}>
                    {intl.formatMessage(
                        { id: "aiCoach.limitsDialogUsage" },
                        {
                            count: usageCount,
                            limit: usageLimit,
                            period: limits.period
                        }
                    )}
                </DialogContentText>
                {usageLimitReached && (
                    <DialogContentText sx={{ mt: 2 }}>
                        {intl.formatMessage({ id: "aiCoach.usageLimitReached" })}
                    </DialogContentText>
                )}
                {rateLimitMinutes > 0 && (
                    <DialogContentText sx={{ mt: 2 }}>
                        {intl.formatMessage({ id: "aiCoach.rateLimitTooltip" }, { minutes: rateLimitMinutes })}
                    </DialogContentText>
                )}
                {(usageLimitReached || rateLimitMinutes > 0) && (
                    <DialogContentText sx={{ mt: 2, fontWeight: "bold" }}>
                        {intl.formatMessage({ id: "aiCoach.limitsDialogContact" })}
                    </DialogContentText>
                )}
            </>
        );
    };

    return (
        <>
            {renderContent()}

            <Dialog
                open={limitsDialogOpen}
                onClose={handleCloseLimitsDialog}
            >
                <DialogTitle>
                    {intl.formatMessage({ id: "aiCoach.limitsDialogTitle" })}
                </DialogTitle>
                <DialogContent>
                    {getLimitsDialogMessage()}
                </DialogContent>
                <DialogActions>
                    {(usageLimitReached || rateLimitMinutes > 0) && (
                        <Button
                            onClick={() => window.location.href = "mailto:support@timeontrails.com"}
                            variant="outlined"
                        >
                            {intl.formatMessage({ id: "aiCoach.contactSupport" })}
                        </Button>
                    )}
                    <Button onClick={handleCloseLimitsDialog} variant="contained" autoFocus>
                        {intl.formatMessage({ id: "action.ok" })}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={!!error}
                message={error ? intl.formatMessage({ id: error }) : ""}
                autoHideDuration={6000}
                onClose={() => setError(null)}
                ContentProps={{
                    sx: {
                        background: "#e51f1f"
                    }
                }}
            />
        </>
    );
});

export default AICoachBox;
